// Introduce-yourself community cards for the festival.

/** Circle invite — dancers join the Broche community forum with this token. */
export const IABF_COMMUNITY_FORUM_URL =
  'https://community.brocheballet.com/join?invitation_token=7615519abf520cda8901a9596b29d3eb2f59bf1d-08d90473-00b8-44cf-86fd-a579fed42d21'

/** WhatsApp group invite from admin (iabf-config-{year}/community). */
export function normalizeWhatsappInviteUrl (raw) {
  let u = String(raw || '').trim()
  if (!u) {
    return ''
  }
  if (!/^https?:\/\//i.test(u)) {
    u = 'https://' + u.replace(/^\/+/, '')
  }
  return u
}

/** Feeling chips dancers can pick (stored as the label string). */
export const INTRO_FEELING_OPTIONS = [
  'Excited',
  'Nervous',
  'Dream come true',
  'Grateful',
  'Ready to grow',
  'A little of everything'
]

export const INTRO_STAY_OPTIONS = [
  { value: 'onsite', label: 'Onsite' },
  { value: 'offsite', label: 'Offsite' }
]

/** How the dancer’s name appears on the community card. */
export const INTRO_NAME_DISPLAY_OPTIONS = [
  { value: 'first', label: 'First name only' },
  { value: 'first_last_initial', label: 'First name + last initial' },
  { value: 'first_last', label: 'First and last name' }
]

export const INTRO_NAME_DISPLAY_DEFAULT = 'first_last'

/**
 * Format community display name from registration names + preference.
 * @param {string} firstName
 * @param {string} lastName
 * @param {string} [preference]
 * @returns {string}
 */
export function formatIntroDisplayName (firstName, lastName, preference) {
  const first = String(firstName || '').trim()
  const last = String(lastName || '').trim()
  const pref = preference || INTRO_NAME_DISPLAY_DEFAULT
  if (pref === 'first') {
    return first || last || 'Dancer'
  }
  if (pref === 'first_last_initial') {
    if (first && last) {
      return `${first} ${last.charAt(0).toUpperCase()}.`
    }
    return first || last || 'Dancer'
  }
  // first_last
  const full = [first, last].filter(Boolean).join(' ')
  return full || 'Dancer'
}

/**
 * Normalize name display preference.
 * @param {string|null|undefined} raw
 * @returns {'first'|'first_last_initial'|'first_last'}
 */
export function normalizeNameDisplayPreference (raw) {
  const v = String(raw || '').trim()
  if (v === 'first' || v === 'first_last_initial' || v === 'first_last') {
    return v
  }
  return INTRO_NAME_DISPLAY_DEFAULT
}

export const INTRO_MAX_COMING_FROM = 80
export const INTRO_MAX_YEARS_DANCING = 80
export const INTRO_MAX_HOTEL = 80
/** Optional free-text “anything else” line on the community card. */
export const INTRO_MAX_NOTE = 160
export const INTRO_NOTE_PLACEHOLDER =
  'e.g. Two rescue dogs, a grandbaby, & a soft spot for Giselle.'

/** Always-on mascot card for the community feed — not a dancer Firestore doc. */
export const ARTIE_INTRO_ID = 'artie-the-armadillo'

export const ARTIE_COMMUNITY_INTRO = {
  id: ARTIE_INTRO_ID,
  displayName: 'Artie the Armadillo',
  firstName: 'Artie',
  lastName: 'Armadillo',
  headshotUrl: '/brand/artie-armadillo.jpg',
  comingFrom: 'New Smyrna Beach, FL',
  yearsDancing: 'My whole life',
  yearsAtIabf: 2,
  showYearsAtIabf: true,
  stayKind: 'onsite',
  showStay: true,
  note: 'Look for me wandering the nature preserve at the ACA.',
  published: true,
  isMascot: true
}

export function isArtieIntro (intro) {
  const i = intro || {}
  return i.id === ARTIE_INTRO_ID || i.isMascot === true
}
export const INTRO_MAX_HEADSHOT_BYTES = 25 * 1024 * 1024 // 25 MB — phone photos often exceed 5

/**
 * Normalize feeling field to a string[] (supports legacy single-string values).
 * @param {string|string[]|null|undefined} raw
 * @returns {string[]}
 */
export function normalizeIntroFeelings (raw) {
  if (Array.isArray(raw)) {
    return raw.map(s => String(s || '').trim()).filter(Boolean)
  }
  const one = String(raw || '').trim()
  return one ? [one] : []
}

/**
 * Infer onsite/offsite from the dancer's registration room field.
 * @param {string} room
 * @returns {'onsite'|'offsite'|''}
 */
export function stayKindFromRoom (room) {
  const r = String(room || '')
  if (!r) {
    return ''
  }
  if (
    r === 'Staying offsite' ||
    r === 'Staying offsite (shuttle)' ||
    r === 'Staying offsite (no shuttle)' ||
    r.toLowerCase().includes('offsite')
  ) {
    return 'offsite'
  }
  // Dorm, cottage, roommate → on campus
  if (
    r === 'Standard Dorm' ||
    r === 'ADA Dorm' ||
    r === 'Cottage' ||
    r === 'Staying with another dancer' ||
    r.toLowerCase().includes('dorm') ||
    r.toLowerCase().includes('cottage')
  ) {
    return 'onsite'
  }
  return ''
}

/**
 * Public stay line for community cards (respects showStay).
 * @param {object} intro
 * @returns {string}
 */
export function introStayLabel (intro) {
  const i = intro || {}
  // Opt-in only — must explicitly show
  if (i.showStay !== true) {
    return ''
  }
  const kind = String(i.stayKind || '').toLowerCase()
  const hotel = String(i.hotel || '').trim()
  if (kind === 'onsite') {
    return hotel ? `Onsite · ${hotel}` : 'Onsite'
  }
  if (kind === 'offsite') {
    return hotel ? `Offsite · ${hotel}` : 'Offsite'
  }
  if (hotel) {
    return hotel
  }
  return ''
}

/**
 * Ordinal for a positive integer: 1 → 1st, 2 → 2nd, 3 → 3rd, 4 → 4th, …
 * @param {number} n
 * @returns {string}
 */
export function ordinalYear (n) {
  const num = Math.round(Number(n))
  if (!Number.isFinite(num) || num <= 0) {
    return ''
  }
  const mod100 = num % 100
  if (mod100 >= 11 && mod100 <= 13) {
    return `${num}th`
  }
  const mod10 = num % 10
  if (mod10 === 1) {
    return `${num}st`
  }
  if (mod10 === 2) {
    return `${num}nd`
  }
  if (mod10 === 3) {
    return `${num}rd`
  }
  return `${num}th`
}

/**
 * Public “years at IABF” line (respects showYearsAtIabf).
 * @param {object} intro
 * @returns {string}
 */
export function introYearsAtIabfLabel (intro) {
  const i = intro || {}
  // Opt-in only — must explicitly show
  if (i.showYearsAtIabf !== true) {
    return ''
  }
  const n = Number(i.yearsAtIabf)
  const ord = ordinalYear(n)
  if (!ord) {
    return ''
  }
  return `${ord} year at IABF`
}

/**
 * Whether an intro should appear on the public feed.
 * All fields are optional — show if they saved a card (published !== false)
 * and have at least a name or any profile content.
 * @param {object} intro
 */
export function introIsPublishable (intro) {
  const i = intro || {}
  if (i.published === false) {
    return false
  }
  // Explicitly published, or has any community content
  if (i.published === true) {
    return true
  }
  return !!(
    String(i.displayName || '').trim() ||
    String(i.firstName || '').trim() ||
    String(i.comingFrom || '').trim() ||
    String(i.yearsDancing || '').trim() ||
    normalizeIntroFeelings(i.feeling).length > 0 ||
    String(i.note || '').trim() ||
    String(i.headshotUrl || '').trim()
  )
}
